import { X } from "lucide-react";
import { Dialog as DrawerPrimitive } from "radix-ui";
import {
  forwardRef,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";

import { cn } from "@/shared/lib/cn";

export const Drawer = DrawerPrimitive.Root;
export const DrawerTrigger = DrawerPrimitive.Trigger;
export const DrawerClose = DrawerPrimitive.Close;
export const DrawerPortal = DrawerPrimitive.Portal;

export const DrawerOverlay = forwardRef<
  ElementRef<typeof DrawerPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    data-slot="drawer-overlay"
    className={cn(
      "fixed inset-0 z-[var(--z-overlay)] bg-black/65 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

type DrawerContentProps = ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Content
> & {
  showCloseButton?: boolean;
};

export const DrawerContent = forwardRef<
  ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ children, className, showCloseButton = true, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      data-slot="drawer-content"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[calc(var(--z-overlay)+1)] mx-auto flex max-h-[88svh] w-full max-w-2xl flex-col rounded-t-[var(--radius-lg)] border border-b-0 border-border bg-background text-foreground shadow-[var(--shadow-overlay)] outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        className,
      )}
      {...props}
    >
      <div
        aria-hidden="true"
        className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border"
      />
      {children}
      {showCloseButton ? (
        <DrawerPrimitive.Close className="absolute top-3 right-3 inline-flex size-8 items-center justify-center rounded-[var(--radius-sm)] border border-transparent text-foreground-muted outline-none hover:bg-surface-raised hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/70 disabled:pointer-events-none disabled:opacity-50">
          <X className="size-4" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </DrawerPrimitive.Close>
      ) : null}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

export function DrawerHeader({
  className,
  ...props
}: ComponentProps<"header">) {
  return (
    <header
      data-slot="drawer-header"
      className={cn(
        "grid shrink-0 gap-1 border-b border-border px-4 py-3 pr-12",
        className,
      )}
      {...props}
    />
  );
}

export const DrawerTitle = forwardRef<
  ElementRef<typeof DrawerPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    data-slot="drawer-title"
    className={cn(
      "m-0 font-mono text-base font-bold tracking-[0.08em] text-warning uppercase",
      className,
    )}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = forwardRef<
  ElementRef<typeof DrawerPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    data-slot="drawer-description"
    className={cn("m-0 text-sm text-foreground-muted", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

export function DrawerBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3",
        className,
      )}
      {...props}
    />
  );
}

export function DrawerFooter({
  className,
  ...props
}: ComponentProps<"footer">) {
  return (
    <footer
      data-slot="drawer-footer"
      className={cn(
        "flex shrink-0 items-center gap-2 border-t border-border px-4 py-3 pb-[calc(0.75rem+var(--safe-bottom))]",
        className,
      )}
      {...props}
    />
  );
}
